import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Loader2, Save, Megaphone } from 'lucide-react';
import { BannerBody } from '../../../components/SiteBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BANNER_TYPES = [
  { value: 'info', label: 'Info (blue)' },
  { value: 'warning', label: 'Warning (yellow)' },
  { value: 'success', label: 'Success (green)' },
  { value: 'danger', label: 'Danger (red)' },
  { value: 'maintenance', label: 'Maintenance (orange)' },
];

const BANNER_SCOPES = [
  { value: 'customer_only', label: 'Customer pages only' },
  { value: 'admin_only', label: 'Admin pages only' },
  { value: 'all', label: 'Both (customer + admin)' },
];

export default function SiteBannerSection() {
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/site-banner`);
      if (!res.ok) throw new Error('Failed to load');
      setBanner(await res.json());
    } catch (e) {
      toast.error('Failed to load banner settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (patch) => setBanner((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!banner) return;
    const text = (banner.message_text || '').trim();
    if (!text) {
      toast.error('Banner message cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/site-banner`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message_text: text,
          banner_type: banner.banner_type,
          show_on: banner.show_on,
          is_active: banner.is_active,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Save failed');
      }
      const data = await res.json();
      setBanner(data.banner);
      toast.success(data.message || 'Banner updated successfully.');
    } catch (e) {
      toast.error(e.message || 'Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="site-banner-section-loading">
        <CardContent className="py-8 text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          <p className="text-sm mt-2">Loading banner settings…</p>
        </CardContent>
      </Card>
    );
  }

  if (!banner) return null;

  return (
    <Card data-testid="site-banner-section">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5" />
              Site Banner
            </CardTitle>
            <CardDescription>
              Control the site-wide announcement banner (message, style, visibility).
              Changes apply instantly without deploy.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="banner-active" className="text-xs uppercase font-bold text-muted-foreground">
              {banner.is_active ? 'ON' : 'OFF'}
            </Label>
            <Switch
              id="banner-active"
              data-testid="banner-active-toggle"
              checked={!!banner.is_active}
              onCheckedChange={(v) => update({ is_active: v })}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Message</Label>
          <Input
            data-testid="banner-message-input"
            value={banner.message_text || ''}
            onChange={(e) => update({ message_text: e.target.value })}
            placeholder="e.g. Maintenance scheduled for Sunday 2 AM."
            maxLength={500}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Banner Type</Label>
            <Select value={banner.banner_type} onValueChange={(v) => update({ banner_type: v })}>
              <SelectTrigger data-testid="banner-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BANNER_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Show On</Label>
            <Select value={banner.show_on} onValueChange={(v) => update({ show_on: v })}>
              <SelectTrigger data-testid="banner-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BANNER_SCOPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">Live Preview</Label>
          <div className="rounded-lg overflow-hidden border" data-testid="banner-preview">
            {banner.message_text?.trim() ? (
              <BannerBody message_text={banner.message_text} banner_type={banner.banner_type} />
            ) : (
              <div className="bg-muted text-muted-foreground py-3 text-center text-sm">
                (Enter a message to preview)
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {banner.is_active
              ? `Currently visible to: ${BANNER_SCOPES.find((s) => s.value === banner.show_on)?.label.toLowerCase()}`
              : 'Banner is OFF — no one sees it.'}
          </p>
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            {banner.updated_by ? `Last updated by ${banner.updated_by}` : ''}
          </p>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="banner-save-button"
            className="btn-primary"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
